import React from "react";

// A render error must never blank the client dashboard to a white screen.
// (translate() throws on a missing dynamic key, and any child component can
// throw on unexpected data.) This boundary converts any such failure into a
// graceful, recoverable card. It is deliberately dependency-light and its own
// fallback copy is guarded so the boundary itself can never throw.
type Variant = "page" | "inline";

interface Props {
  t: (key: string) => string;
  children?: React.ReactNode;
  variant?: Variant;
  // Changing this value resets a failed boundary (e.g. when the active surface
  // changes), so a user can navigate away from a broken view.
  resetKey?: string | number;
}

interface State {
  failed: boolean;
}

const FALLBACK_COPY: Record<string, string> = {
  "errors.crashTitle": "Něco se nepovedlo",
  "errors.crashBody": "Zkus prosím načíst stránku znovu.",
  "errors.reload": "Načíst znovu"
};

function safeCopy(t: (key: string) => string, key: string): string {
  try {
    return t(key);
  } catch {
    return FALLBACK_COPY[key] ?? key;
  }
}

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { failed: false };

  static getDerivedStateFromError(): State {
    return { failed: true };
  }

  componentDidUpdate(previous: Props): void {
    if (this.state.failed && previous.resetKey !== this.props.resetKey) {
      this.setState({ failed: false });
    }
  }

  componentDidCatch(error: unknown): void {
    try {
      console.error(JSON.stringify({
        event: "ui_error_boundary",
        variant: this.props.variant ?? "page",
        error: error instanceof Error ? error.name : "unknown"
      }));
    } catch {
      // Telemetry must never mask the original error.
    }
  }

  render(): React.ReactNode {
    if (!this.state.failed) return this.props.children;
    const t = this.props.t;
    return (
      <div className={this.props.variant === "inline" ? "app-error app-error-inline" : "app-error"} role="alert">
        <p className="app-error-title">{safeCopy(t, "errors.crashTitle")}</p>
        <p className="app-error-body">{safeCopy(t, "errors.crashBody")}</p>
        <button className="primary-button" type="button" onClick={() => window.location.reload()}>
          {safeCopy(t, "errors.reload")}
        </button>
      </div>
    );
  }
}
